// Раздел «Задания процессов»: что висит на людях в бизнес-процессах Bitrix.
// ТОЛЬКО ЧТЕНИЕ — ни одной кнопки, меняющей Bitrix, здесь нет и быть не может.
//
// Почему нельзя закрыть задание отсюда: bizproc.task.complete выполняет ТОЛЬКО СВОЁ
// задание — Bitrix ищет его среди заданий автора REST-запроса. CRM ходит служебным
// вебхуком, значит чужое задание она не найдёт вовсе, а своё закрыла бы от имени живого
// человека. Нужна персональная OAuth-привязка каждого сотрудника: отдельный шаг,
// отдельное решение владельца. До тех пор — ссылка «в Bitrix».
//
// Пять вкладок, и порядок у них не случайный. Первым идёт «Кто ждёт», потому что
// главный вопрос раздела — не «какие бывают задания», а «на ком встала работа»:
// на 27.07.2026 из 6 998 живых заданий 1 222 висят на одном человеке, а 968 — на 70
// отключённых учётках, то есть не будут закрыты никогда. Вкладка «На уволенных»
// отвечает на следующий за этим вопрос — сколько денег стоит за такой остановкой.

import { useMemo, useState } from 'react'

import type { ЗаданиеСтрока, УволСтрока, ЧеловекСтрока, ШаблонСтрока, ЭкземплярСтрока }
  from '../api/bp'
import {
  useЗадания, useНаУволенных, useПоЛюдям, useСправочники, useШаблоны, useЭкземпляры,
} from '../api/bp'
import { EntityLink } from '../components/EntityLink'
import { FilterBar } from '../components/FilterBar'
import { QueryState } from '../components/QueryState'
import { fmtDate, fmtMoney, fmtText, pluralN } from '../lib/format'
import {
  АВТОЗАПУСК, РАЗМЕР_СТРАНИЦЫ, видЗадания, видОжидания, видСотрудника, названиеДокумента,
  почемуТолькоВBitrix, ссылкаВBitrix,
} from '../lib/bp'

type Вкладка = 'people' | 'left' | 'tasks' | 'instances' | 'templates'

const ВКЛАДКИ: { код: Вкладка; имя: string }[] = [
  { код: 'people', имя: 'Кто ждёт' },
  { код: 'left', имя: 'На уволенных' },
  { код: 'tasks', имя: 'Задания' },
  { код: 'instances', имя: 'Процессы' },
  { код: 'templates', имя: 'Шаблоны' },
]

export default function BpTasks() {
  const [вкладка, setВкладка] = useState<Вкладка>('people')
  const [порог, setПорог] = useState(30)
  const спр = useСправочники()
  const с = спр.data

  return (
    <>
      <div className="page-head">
        <h1>Задания процессов</h1>
        <span className="spacer" />
        <span className="hint">
          {с ? `висит ${pluralN(с.counts.tasks_live ?? 0, 'ожидание', 'ожидания', 'ожиданий')}`
            + ` на ${pluralN(с.counts.tasks_live_distinct ?? 0, 'задании', 'заданиях', 'заданиях')},`
            + ` ${pluralN(с.counts.people ?? 0, 'сотрудник', 'сотрудника', 'сотрудников')}` : ''}
          {с?.counts.instances ? ` · процессов запущено ${с.counts.instances}` : ''}
        </span>
      </div>

      {/* Оговорки приходят от ядра вместе с данными и показываются ДО них. Экран, который
          их спрячет, соврёт молча: «просрочено» тут не то же самое, что в почте, а история
          закрытых заданий начинается не с начала времён. */}
      {с && с.caveats.length > 0 && (
        <div className="notice" style={{ marginBottom: 16 }}>
          <b>Как это читать.</b>
          <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
            {с.caveats.map((т) => (
              <li key={т}>{т}</li>
            ))}
          </ul>
          {с.counts.history_from && (
            <div style={{ marginTop: 6 }}>
              История закрытых заданий в CRM ведётся с {fmtDate(с.counts.history_from)}.
            </div>
          )}
        </div>
      )}

      <div className="card" style={{ marginBottom: 16 }}>
        {/* Вкладки кнопками в .filters — тем же способом, что в «Кабинете закупщика»:
            своего класса под вкладки в styles.css нет, и заводить его ради одного экрана
            значило бы развести два разных вида вкладок в одной CRM. */}
        <div className="filters">
          {ВКЛАДКИ.map((в) => (
            <button key={в.код} type="button" onClick={() => setВкладка(в.код)}
                    disabled={вкладка === в.код}>
              {в.имя}
            </button>
          ))}
        </div>
        <div className="hint" style={{ marginTop: 8 }}>
          Раздел только показывает. Закрыть, отложить или передать задание можно в Bitrix —
          ссылка есть у каждой строки.
        </div>
      </div>

      {вкладка === 'people' && <ПоЛюдям порог={порог} setПорог={setПорог} />}
      {вкладка === 'left' && <НаУволенных />}
      {вкладка === 'tasks' && <Задания порог={порог} />}
      {вкладка === 'instances' && <Экземпляры />}
      {вкладка === 'templates' && <Шаблоны />}
    </>
  )
}

// ─────────────────────────── вкладка «Кто ждёт» ───────────────────────────

function ПоЛюдям({ порог, setПорог }: { порог: number; setПорог: (v: number) => void }) {
  const запрос = useПоЛюдям(useMemo(() => ({ old_days: порог }), [порог]))
  const д = запрос.data

  return (
    <>
      <div className="card" style={{ marginBottom: 16 }}>
        <FilterBar id="bp-people">
          <div className="f">
            <label htmlFor="bpold">Считать «висит долго» от</label>
            <select id="bpold" value={порог} onChange={(e) => setПорог(Number(e.target.value))}>
              <option value={7}>7 дней</option>
              <option value={14}>14 дней</option>
              <option value={30}>30 дней</option>
              <option value={90}>90 дней</option>
              <option value={180}>180 дней</option>
            </select>
          </div>
        </FilterBar>
      </div>

      <QueryState isLoading={запрос.isLoading} error={запрос.error}>
        {д && д.totals.left_tasks > 0 && (
          // Отдельной строкой, а не колонкой в таблице: задание на отключённой учётке
          // не закроет никто, и процесс за ним стоит навсегда. Это не «ещё одна метрика».
          <div className="notice" style={{ marginBottom: 16 }}>
            <b>{pluralN(д.totals.left_tasks, 'задание висит', 'задания висят', 'заданий висят')}</b>
            {' '}на {pluralN(д.totals.left_people, 'отключённой учётке', 'отключённых учётках',
              'отключённых учётках')}. Такое задание не закроет никто: закрыть его может только сам
            исполнитель, а он уже не работает. Процессы за ними стоят до тех пор, пока
            администратор Bitrix не передаст задания живым людям.
          </div>
        )}

        <div className="card">
          <div className="table-wrap cards">
            <table>
              <thead>
                <tr>
                  <th>Сотрудник</th>
                  <th className="num">Заданий</th>
                  <th className="num">Согласовать</th>
                  <th className="num">Ознакомиться</th>
                  <th className="num">Заполнить</th>
                  <th className="num">Дольше {порог} дн.</th>
                  <th className="num">Просрочено</th>
                  <th className="num">Медиана</th>
                  <th className="num">Самое старое</th>
                </tr>
              </thead>
              <tbody>
                {(д?.rows ?? []).map((r) => <СтрокаЧеловека key={r.user_id ?? 0} r={r} />)}
                {д && д.rows.length === 0 && (
                  <tr>
                    <td colSpan={9} className="empty">
                      <div className="empty-title">Живых заданий в зеркале нет.</div>
                      <div className="empty-hint">
                        Если это неожиданно — посмотрите свежесть зеркала bp:tasks: пустой
                        список тут значит «синк не отработал», а не «работы нет».
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
              {д && д.rows.length > 0 && (
                <tfoot>
                  <tr>
                    <th data-label="Итого">Итого: {д.totals.people} чел.</th>
                    <th className="num" data-label="Заданий">{д.totals.tasks}</th>
                    <th data-label="" />
                    <th data-label="" />
                    <th data-label="" />
                    <th data-label="" />
                    <th data-label="" />
                    <th data-label="" />
                    <th data-label="" />
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      </QueryState>
    </>
  )
}

function СтрокаЧеловека({ r }: { r: ЧеловекСтрока }) {
  const ушёл = видСотрудника(r.user_active)
  return (
    <tr>
      <td data-label="Сотрудник">
        {fmtText(r.user_name)}
        {ушёл && <span className={ушёл.класс} style={{ marginLeft: 6 }}>{ушёл.ярлык}</span>}
      </td>
      <td className="num" data-label="Заданий">{r.tasks}</td>
      <td className="num" data-label="Согласовать">{r.approve}</td>
      <td className="num" data-label="Ознакомиться">{r.review}</td>
      <td className="num" data-label="Заполнить">{r.fill}</td>
      <td className="num" data-label="Висит долго">{r.old}</td>
      {/* Просрочка Bitrix, а не наша: у большинства заданий срока нет вовсе, и ноль здесь
          означает «срок не назначен», а не «успевает». Так и подписано в оговорках. */}
      <td className="num" data-label="Просрочено">{r.overdue}</td>
      <td className="num" data-label="Медиана ожидания">
        {r.median_days === null ? '—' : `${r.median_days} дн.`}
      </td>
      <td className="num" data-label="Самое старое">
        {r.oldest_days === null ? '—' : `${r.oldest_days} дн.`}
      </td>
    </tr>
  )
}

// ─────────────────────────── вкладка «На уволенных» ───────────────────────────
//
// Задание на отключённой учётке не закроет никто, и процесс за ним стоит навсегда.
// Эта вкладка отвечает на вопрос «сколько денег стоит за этой остановкой»: суммы
// связанных сделок, открытые отдельно от закрытых, валюты отдельно друг от друга.
// Все числа приходят из /bp/left тем же запросом — на экране нет ни одного вбитого.

function НаУволенных() {
  const запрос = useНаУволенных()
  const д = запрос.data
  const открытыеРуб = (д?.money ?? []).find((м) => м.closed === false && м.currency === 'RUB')

  return (
    <QueryState isLoading={запрос.isLoading} error={запрос.error}>
      {д && д.totals.waits > 0 && (
        <div className="notice" style={{ marginBottom: 16 }}>
          <b>{pluralN(д.totals.waits, 'ожидание висит', 'ожидания висят', 'ожиданий висят')}</b>
          {' '}({pluralN(д.totals.tasks, 'задание', 'задания', 'заданий')})
          {' '}на {pluralN(д.totals.people, 'отключённой учётке', 'отключённых учётках',
            'отключённых учётках')}.
          {' '}Со сделками связано {pluralN(д.totals.waits_with_deal, 'ожидание',
            'ожидания', 'ожиданий')} — это {pluralN(д.totals.deals, 'сделка', 'сделки',
            'сделок')}{открытыеРуб?.amount
            ? <>, из них открытых на <b>{fmtMoney(открытыеРуб.amount)}</b></> : ''}.
          {' '}Такое задание не закроет никто: закрыть его может только сам исполнитель,
          а он уже не работает. Передать задания живым людям может администратор Bitrix.
        </div>
      )}

      {д && (д.money.length > 0) && (
        <div className="card" style={{ marginBottom: 16 }}>
          <h2>Деньги за вставшими процессами</h2>
          <div className="table-wrap cards">
            <table>
              <thead>
                <tr>
                  <th>Сделки</th>
                  <th className="nowrap">Валюта</th>
                  <th className="num">Сколько</th>
                  <th className="num">На сумму</th>
                </tr>
              </thead>
              <tbody>
                {д.money.map((м) => (
                  <tr key={`${м.closed}-${м.currency}`}>
                    <td data-label="Сделки">
                      {м.closed
                        ? <span className="muted">закрытые</span>
                        : <b>открытые</b>}
                    </td>
                    <td className="nowrap" data-label="Валюта">{fmtText(м.currency)}</td>
                    <td className="num" data-label="Сколько">{м.deals}</td>
                    <td className="num" data-label="На сумму">
                      {м.amount === null ? '—' : fmtMoney(м.amount, м.currency ?? 'RUB')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="hint" style={{ marginTop: 8 }}>{д.note}</div>
        </div>
      )}

      <div className="card">
        <div className="table-wrap cards">
          <table>
            <thead>
              <tr>
                <th>Сотрудник</th>
                <th className="num">Ожиданий</th>
                <th className="num">Сделок</th>
                <th className="num">Открытых сделок</th>
                <th className="num">В открытых сделках</th>
                <th className="num">Самое старое</th>
              </tr>
            </thead>
            <tbody>
              {(д?.rows ?? []).map((r) => <СтрокаУволенного key={r.user_id ?? 0} r={r} />)}
              {д && д.rows.length === 0 && (
                <tr>
                  <td colSpan={6} className="empty">
                    <div className="empty-title">
                      На отключённых учётках живых заданий нет.
                    </div>
                    <div className="empty-hint">
                      Это хорошая новость, если зеркало свежее: посмотрите свежесть
                      bp:tasks на вкладке «Кто ждёт».
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="hint" style={{ marginTop: 8 }}>
          Сами задания — на вкладке «Задания» с галочкой «на тех, кто не работает».
        </div>
      </div>
    </QueryState>
  )
}

function СтрокаУволенного({ r }: { r: УволСтрока }) {
  return (
    <tr>
      <td data-label="Сотрудник">
        {fmtText(r.user_name)}
        {/* Ярлык здесь один и всегда: на этой вкладке других не бывает по определению. */}
        <span className="badge lose" style={{ marginLeft: 6 }}>не работает</span>
      </td>
      <td className="num" data-label="Ожиданий">{r.tasks}</td>
      <td className="num" data-label="Сделок">{r.deals || '—'}</td>
      <td className="num" data-label="Открытых сделок">{r.open_deals || '—'}</td>
      <td className="num" data-label="В открытых сделках">
        {r.open_rub === null ? '—' : fmtMoney(r.open_rub)}
        {r.open_other > 0 && (
          <div className="badge work"
               title="сумма не показана: валюту со сложением в рубли зеркало не мешает">
            ещё {pluralN(r.open_other, 'сделка', 'сделки', 'сделок')} не в рублях
          </div>
        )}
      </td>
      <td className="num" data-label="Самое старое">
        {r.oldest_days === null ? '—' : `${r.oldest_days} дн.`}
      </td>
    </tr>
  )
}

// ─────────────────────────── вкладка «Задания» ───────────────────────────

function Задания({ порог }: { порог: number }) {
  const спр = useСправочники()
  const [user, setUser] = useState('')
  const [activity, setActivity] = useState('')
  const [docKind, setDocKind] = useState('')
  const [q, setQ] = useState('')
  const [old, setOld] = useState(false)
  const [left, setLeft] = useState(false)
  const [start, setStart] = useState(0)

  const фильтры = useMemo(() => ({
    user: user || undefined,
    activity: activity || undefined,
    doc_kind: docKind || undefined,
    q: q.trim() || undefined,
    old: old || undefined,
    left: left || undefined,
    old_days: порог,
    start,
    limit: РАЗМЕР_СТРАНИЦЫ,
  }), [user, activity, docKind, q, old, left, порог, start])

  const запрос = useЗадания(фильтры)
  const д = запрос.data
  const сСброса = <T,>(setter: (v: T) => void) => (v: T) => { setStart(0); setter(v) }

  return (
    <>
      <div className="card" style={{ marginBottom: 16 }}>
        <FilterBar id="bp-tasks">
          <div className="f" style={{ flex: 2, minWidth: 220 }}>
            <label htmlFor="bpq">Задание, документ или процесс</label>
            <input id="bpq" value={q} onChange={(e) => сСброса(setQ)(e.target.value)}
                   placeholder="например: счет или Отгрузка" />
          </div>
          <div className="f" style={{ minWidth: 200 }}>
            <label htmlFor="bpu">Сотрудник</label>
            <select id="bpu" value={user} onChange={(e) => сСброса(setUser)(e.target.value)}
                    disabled={!спр.data}>
              <option value="">любой</option>
              {(спр.data?.people ?? []).map((p) => (
                <option key={p.id ?? 0} value={p.id ?? ''}>
                  {p.name} ({p.live}){p.active === false ? ' — не работает' : ''}
                </option>
              ))}
            </select>
          </div>
          <div className="f">
            <label htmlFor="bpa">Тип задания</label>
            <select id="bpa" value={activity}
                    onChange={(e) => сСброса(setActivity)(e.target.value)}>
              <option value="">любой</option>
              {(спр.data?.activities ?? []).map((a) => (
                <option key={a.activity ?? ''} value={a.activity ?? ''}>
                  {видЗадания(a.activity).ярлык} ({a.n})
                </option>
              ))}
            </select>
          </div>
          <div className="f">
            <label htmlFor="bpk">Вид документа</label>
            <select id="bpk" value={docKind}
                    onChange={(e) => сСброса(setDocKind)(e.target.value)}>
              <option value="">любой</option>
              {Object.entries(спр.data?.doc_kind_names ?? {}).map(([к, н]) => (
                <option key={к} value={к}>{н}</option>
              ))}
            </select>
          </div>
          <div className="f" style={{ minWidth: 150 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 18 }}>
              <input type="checkbox" checked={old}
                     onChange={(e) => сСброса(setOld)(e.target.checked)}
                     style={{ width: 'auto' }} />
              висят дольше {порог} дн.
            </label>
          </div>
          <div className="f" style={{ minWidth: 170 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 18 }}>
              <input type="checkbox" checked={left}
                     onChange={(e) => сСброса(setLeft)(e.target.checked)}
                     style={{ width: 'auto' }} />
              на тех, кто не работает
            </label>
          </div>
        </FilterBar>
      </div>

      <QueryState isLoading={запрос.isLoading} error={запрос.error}>
        <div className="card">
          <div className="table-wrap cards">
            <table>
              <thead>
                <tr>
                  <th>Что ждут</th>
                  <th className="nowrap">Тип</th>
                  <th className="nowrap">На ком</th>
                  <th>Документ</th>
                  <th className="nowrap">Процесс</th>
                  <th className="num">Висит</th>
                  <th className="nowrap">В Bitrix</th>
                </tr>
              </thead>
              <tbody>
                {(д?.rows ?? []).map((r) => (
                  <СтрокаЗадания key={r.row_key} r={r} порог={порог} />
                ))}
                {д && д.rows.length === 0 && (
                  <tr>
                    <td colSpan={7} className="empty">
                      <div className="empty-title">Под эти фильтры заданий нет</div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {д && д.total > РАЗМЕР_СТРАНИЦЫ && (
            <div className="pager">
              <button disabled={start === 0 || запрос.isFetching}
                      onClick={() => setStart(Math.max(0, start - РАЗМЕР_СТРАНИЦЫ))}>
                ← назад
              </button>
              <span className="muted">
                {start + 1}–{Math.min(start + РАЗМЕР_СТРАНИЦЫ, д.total)} из {д.total}
              </span>
              <button disabled={д.next === null || запрос.isFetching}
                      onClick={() => setStart(д.next!)}>вперёд →</button>
            </div>
          )}
        </div>
      </QueryState>
    </>
  )
}

function СтрокаЗадания({ r, порог }: { r: ЗаданиеСтрока; порог: number }) {
  const тип = видЗадания(r.activity)
  const ждёт = видОжидания(r.days_waiting, порог)
  const ушёл = видСотрудника(r.user_active)
  const ссылка = ссылкаВBitrix(r.document_url)
  return (
    <tr>
      <td data-label="Что ждут" title={почемуТолькоВBitrix(r.req_file_field)}>
        {fmtText(r.name)}
        {r.req_file_field && (
          <span className="badge" style={{ marginLeft: 6 }} title="через REST не закрыть ничем">
            нужен файл
          </span>
        )}
      </td>
      <td className="nowrap" data-label="Тип">
        <span className={тип.класс} title={тип.смысл}>{тип.ярлык}</span>
      </td>
      <td className="nowrap" data-label="На ком">
        {fmtText(r.user_name)}
        {ушёл && <span className={ушёл.класс} style={{ marginLeft: 6 }}>{ушёл.ярлык}</span>}
      </td>
      <td data-label="Документ">
        {/* Ссылка внутрь CRM есть только у сделки: остальные виды (смарт-процессы, списки
            бизнес-процессов) в CRM пока не перенесены, и уводить туда некуда. Пишем, что
            это за документ, и даём ссылку в Bitrix соседней колонкой. */}
        {r.deal_id ? (
          <EntityLink kind="deal" id={r.deal_id} title={r.deal_title || r.document_name} />
        ) : (
          <>
            <span className="muted">{названиеДокумента(r.doc_kind, r.doc_type_id)}</span>{' '}
            {fmtText(r.document_name)}
          </>
        )}
      </td>
      <td className="nowrap muted" data-label="Процесс" title={r.workflow_state || ''}>
        {fmtText(r.template_name)}
      </td>
      <td className="num" data-label="Висит">
        <span className={ждёт.класс}>{ждёт.ярлык}</span>
        {r.overdue_bitrix && (
          <div className="badge lose" title={`срок ${fmtDate(r.overdue_date)}`}>просрочено</div>
        )}
      </td>
      <td className="nowrap" data-label="В Bitrix">
        {ссылка ? (
          <a href={ссылка} target="_blank" rel="noreferrer">открыть ↗</a>
        ) : (
          <span className="muted">—</span>
        )}
      </td>
    </tr>
  )
}

// ─────────────────────────── вкладка «Процессы» ───────────────────────────

function Экземпляры() {
  const [idle, setIdle] = useState(false)
  const [start, setStart] = useState(0)
  const фильтры = useMemo(() => ({
    idle: idle || undefined, start, limit: РАЗМЕР_СТРАНИЦЫ,
  }), [idle, start])
  const запрос = useЭкземпляры(фильтры)
  const д = запрос.data

  return (
    <>
      <div className="card" style={{ marginBottom: 16 }}>
        <FilterBar id="bp-instances">
          <div className="f" style={{ minWidth: 230 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 18 }}>
              <input type="checkbox" checked={idle}
                     onChange={(e) => { setStart(0); setIdle(e.target.checked) }}
                     style={{ width: 'auto' }} />
              только те, что не ждут никого
            </label>
          </div>
        </FilterBar>
        {/* Ровно та работа, которую в Bitrix не видно нигде: в «моих заданиях» такого
            процесса нет по определению, а списка процессов у сотрудника не существует. */}
        <div className="hint" style={{ marginTop: 8 }}>
          Процесс без заданий висит на таймере или на условии, а не на человеке. В интерфейсе
          Bitrix его не видно ни на одном экране — поэтому он и здесь.
        </div>
      </div>

      <QueryState isLoading={запрос.isLoading} error={запрос.error}>
        <div className="card">
          <div className="table-wrap cards">
            <table>
              <thead>
                <tr>
                  <th className="nowrap">Процесс</th>
                  <th>Документ</th>
                  <th className="nowrap">Состояние</th>
                  <th className="num">Заданий</th>
                  <th className="num">Идёт</th>
                  <th className="nowrap">Начат</th>
                </tr>
              </thead>
              <tbody>
                {(д?.rows ?? []).map((r) => <СтрокаЭкземпляра key={r.workflow_id} r={r} />)}
                {д && д.rows.length === 0 && (
                  <tr>
                    <td colSpan={6} className="empty">
                      <div className="empty-title">Под эти условия запущенных процессов нет</div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {д && д.total > РАЗМЕР_СТРАНИЦЫ && (
            <div className="pager">
              <button disabled={start === 0 || запрос.isFetching}
                      onClick={() => setStart(Math.max(0, start - РАЗМЕР_СТРАНИЦЫ))}>
                ← назад
              </button>
              <span className="muted">
                {start + 1}–{Math.min(start + РАЗМЕР_СТРАНИЦЫ, д.total)} из {д.total}
              </span>
              <button disabled={д.next === null || запрос.isFetching}
                      onClick={() => setStart(д.next!)}>вперёд →</button>
            </div>
          )}
        </div>
      </QueryState>
    </>
  )
}

function СтрокаЭкземпляра({ r }: { r: ЭкземплярСтрока }) {
  return (
    <tr>
      <td className="nowrap" data-label="Процесс">
        {fmtText(r.template_name)}
        {r.is_dead_name && (
          <span className="badge lose" style={{ marginLeft: 6 }}
                title="шаблон помечен мёртвым в названии, а процесс запущен">
            помечен мёртвым
          </span>
        )}
      </td>
      <td data-label="Документ">
        {r.deal_id ? (
          <EntityLink kind="deal" id={r.deal_id} title={r.deal_title} />
        ) : (
          <span className="muted">
            {названиеДокумента(r.doc_kind, r.doc_type_id)}
            {r.doc_item_id ? ` #${r.doc_item_id}` : ''}
          </span>
        )}
      </td>
      {/* Состояние процесса REST не отдаёт вовсе. Здесь — то, что записано в его же
          заданиях; у процесса без заданий его нет, и прочерк тут значит «неизвестно». */}
      <td className="nowrap muted" data-label="Состояние">
        {r.state_guess ? fmtText(r.state_guess) : 'неизвестно'}
      </td>
      <td className="num" data-label="Заданий">{r.live_tasks}</td>
      <td className="num" data-label="Идёт">
        {r.days_running === null ? '—' : `${r.days_running} дн.`}
      </td>
      <td className="nowrap muted" data-label="Начат">{fmtDate(r.started)}</td>
    </tr>
  )
}

// ─────────────────────────── вкладка «Шаблоны» ───────────────────────────

function Шаблоны() {
  const [kind, setKind] = useState('')
  const [q, setQ] = useState('')
  const [start, setStart] = useState(0)
  const фильтры = useMemo(() => ({
    kind: kind || undefined, q: q.trim() || undefined, start, limit: РАЗМЕР_СТРАНИЦЫ,
  }), [kind, q, start])
  const запрос = useШаблоны(фильтры)
  const д = запрос.data

  return (
    <>
      <div className="card" style={{ marginBottom: 16 }}>
        <FilterBar id="bp-templates">
          <div className="f" style={{ flex: 2, minWidth: 220 }}>
            <label htmlFor="bptq">Название шаблона или тип документа</label>
            <input id="bptq" value={q} onChange={(e) => { setStart(0); setQ(e.target.value) }}
                   placeholder="например: Маржа или DEAL" />
          </div>
          <div className="f" style={{ minWidth: 220 }}>
            <label htmlFor="bptk">Показать</label>
            <select id="bptk" value={kind}
                    onChange={(e) => { setStart(0); setKind(e.target.value) }}>
              <option value="">все шаблоны</option>
              <option value="live">работающие</option>
              <option value="never">ни разу не запускались</option>
              <option value="zombie">помечены мёртвыми, но работают</option>
              <option value="hidden">роботы и триггеры воронок</option>
            </select>
          </div>
        </FilterBar>
        <div className="hint" style={{ marginTop: 8 }}>
          «Ни разу не запускался» считается по счётчикам заданий из Bitrix. У шаблонов, по
          которым счётчик ещё не снимали, такой вывод не сделан вовсе — прочерк здесь значит
          «не проверяли», а не «не запускался». Строки «нет в перечне» — роботы и триггеры
          воронок: Bitrix не отдаёт их в списке шаблонов вообще, и найдены они по самим
          заданиям, поэтому у части из них нет даже названия.
        </div>
      </div>

      <QueryState isLoading={запрос.isLoading} error={запрос.error}>
        <div className="card">
          <div className="table-wrap cards">
            <table>
              <thead>
                <tr>
                  <th className="nowrap">ID</th>
                  <th>Название</th>
                  <th className="nowrap">Документ</th>
                  <th className="nowrap">Запуск</th>
                  <th className="num">Заданий всего</th>
                  <th className="num">Висит сейчас</th>
                  <th className="num">Запущено</th>
                  <th className="nowrap">Счётчики сняты</th>
                </tr>
              </thead>
              <tbody>
                {(д?.rows ?? []).map((r) => <СтрокаШаблона key={r.bitrix_id} r={r} />)}
                {д && д.rows.length === 0 && (
                  <tr>
                    <td colSpan={8} className="empty">
                      <div className="empty-title">Под эти условия шаблонов нет</div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {д && д.total > РАЗМЕР_СТРАНИЦЫ && (
            <div className="pager">
              <button disabled={start === 0 || запрос.isFetching}
                      onClick={() => setStart(Math.max(0, start - РАЗМЕР_СТРАНИЦЫ))}>
                ← назад
              </button>
              <span className="muted">
                {start + 1}–{Math.min(start + РАЗМЕР_СТРАНИЦЫ, д.total)} из {д.total}
              </span>
              <button disabled={д.next === null || запрос.isFetching}
                      onClick={() => setStart(д.next!)}>вперёд →</button>
            </div>
          )}
        </div>
      </QueryState>
    </>
  )
}

function СтрокаШаблона({ r }: { r: ШаблонСтрока }) {
  return (
    <tr>
      <td className="mono nowrap" data-label="ID">{r.bitrix_id}</td>
      <td data-label="Название">
        {fmtText(r.name)}
        {r.dead_but_alive && (
          <span className="badge lose" style={{ marginLeft: 6 }}
                title="в названии помечен мёртвым, но задания по нему висят">
            помечен мёртвым, а работает
          </span>
        )}
        {r.never_ran && (
          <span className="badge" style={{ marginLeft: 6 }}
                title="ни одного задания за всю историю и ни одного запущенного экземпляра">
            ни разу не запускался
          </span>
        )}
        {r.listed === false && (
          <span className="badge work" style={{ marginLeft: 6 }}
                title="bizproc.workflow.template.list такие не отдаёт: это робот или триггер воронки">
            нет в перечне шаблонов
          </span>
        )}
        {!r.name && <span className="muted">названия нет — Bitrix его не отдал</span>}
      </td>
      <td className="nowrap muted" data-label="Документ">{fmtText(r.doc_type)}</td>
      <td className="nowrap muted" data-label="Запуск">
        {r.auto_execute === null ? '—' : АВТОЗАПУСК[r.auto_execute] ?? `код ${r.auto_execute}`}
      </td>
      {/* Прочерк, а не ноль: счётчик не снимали — это не «шаблон не работал». Разница
          принципиальная, по ней принимают решение «можно ли удалить». */}
      <td className="num" data-label="Заданий всего">
        {r.tasks_all === null ? '—' : r.tasks_all}
      </td>
      <td className="num" data-label="Висит сейчас">
        {r.tasks_live === null ? '—' : r.tasks_live}
      </td>
      <td className="num" data-label="Запущено">
        {r.instances_live === null ? '—' : r.instances_live}
      </td>
      <td className="nowrap muted" data-label="Счётчики сняты">
        {r.stat_at ? fmtDate(r.stat_at) : 'не снимали'}
      </td>
    </tr>
  )
}
